import { useState, type FormEvent } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, X } from 'lucide-react';
import type {
  TicketChecklist,
  TicketChecklistAddBody,
  TicketDetail,
} from '@erp/shared';
import { api, apiList } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { cn, formatDateTime } from '@/lib/utils';
import { deskChecklistTemplatesOptions } from '@/features/desks/desks-queries';
import { teamListQuery } from '@/features/team/team-queries';
import { errorMessage, isTicketReadOnly } from './ticket-format';

function ticketChecklistsQuery(ticketNumber: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'checklists', { ticketNumber }] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<TicketChecklist>(`/tickets/${ticketNumber}/checklists`, { signal });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

/**
 * Aba "Checklists" do ticket: listas com progresso (n/m + barra fina), toggle
 * por item (mostra quem/quando marcou), adicionar do template da mesa ou
 * ad-hoc, adicionar/remover item e remover checklist. Somente leitura quando
 * o ticket está fechado/cancelado.
 */
export function ChecklistsTab({ ticket }: { ticket: TicketDetail }) {
  const queryClient = useQueryClient();
  const ticketNumber = ticket.ticketNumber;
  // fechado/cancelado OU espelhado do TiFlux (spec 011)
  const readOnly = isTicketReadOnly(ticket);

  const listQ = useQuery(ticketChecklistsQuery(ticketNumber));
  // quem marcou é sempre alguém da equipe: o nome sai da equipe do tenant,
  // inativos inclusive — GET /users é global e cortava em 200
  const usersQ = useQuery(teamListQuery({ includeInactive: true }));
  const [addOpen, setAddOpen] = useState(false);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ticketChecklistsQuery(ticketNumber).queryKey });

  const removeChecklist = useMutation({
    mutationFn: async (checklistId: number) => {
      const res = await api<void>(`/tickets/checklists/${checklistId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const usersById = new Map((usersQ.data?.items ?? []).map((u) => [u.id, u.name]));
  const checklists = listQ.data ?? [];

  return (
    <div className="space-y-4">
      <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Checklists</h2>
          {!readOnly ? (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="size-4" strokeWidth={2.5} />
              Adicionar checklist
            </Button>
          ) : null}
        </div>

        {listQ.isPending ? (
          <div className="space-y-3" aria-busy>
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : listQ.isError ? (
          <EmptyState message={`Erro ao carregar checklists: ${errorMessage(listQ.error)}`} />
        ) : checklists.length === 0 ? (
          <EmptyState
            className="h-28"
            message={
              readOnly ? 'Nenhum checklist neste ticket' : 'Nenhum checklist ainda — adicione um'
            }
          />
        ) : (
          <div className="space-y-4">
            {checklists.map((cl) => (
              <ChecklistCard
                key={cl.id}
                checklist={cl}
                readOnly={readOnly}
                userName={(id) => usersById.get(id) ?? `Usuário #${id}`}
                onRemove={() => {
                  if (window.confirm(`Remover o checklist "${cl.name}"?`))
                    removeChecklist.mutate(cl.id);
                }}
                onChanged={invalidate}
              />
            ))}
          </div>
        )}
        {removeChecklist.error ? (
          <p className="mt-2 text-sm text-destructive">{errorMessage(removeChecklist.error)}</p>
        ) : null}
      </section>

      {addOpen ? (
        <AddChecklistDialog
          ticketNumber={ticketNumber}
          deskId={ticket.desk.id}
          onClose={() => setAddOpen(false)}
          onAdded={invalidate}
        />
      ) : null}
    </div>
  );
}

function ChecklistCard({
  checklist,
  readOnly,
  userName,
  onRemove,
  onChanged,
}: {
  checklist: TicketChecklist;
  readOnly: boolean;
  userName: (id: number) => string;
  onRemove: () => void;
  onChanged: () => void;
}) {
  const [newItem, setNewItem] = useState('');

  const toggle = useMutation({
    mutationFn: async (itemId: number) => {
      const res = await api<unknown>(`/tickets/checklists/items/${itemId}/toggle`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
    },
    onSuccess: onChanged,
  });

  const addItem = useMutation({
    mutationFn: async (text: string) => {
      const res = await api<unknown>(`/tickets/checklists/${checklist.id}/items`, {
        method: 'POST',
        body: { text },
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      setNewItem('');
      onChanged();
    },
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: number) => {
      const res = await api<void>(`/tickets/checklists/items/${itemId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: onChanged,
  });

  const items = [...checklist.items].sort((a, b) => a.position - b.position);
  const done = items.filter((i) => i.done).length;
  const pct = items.length === 0 ? 0 : Math.round((done / items.length) * 100);
  const mutating = toggle.isPending || addItem.isPending || removeItem.isPending;
  const error = toggle.error ?? addItem.error ?? removeItem.error;

  const submitNewItem = (e: FormEvent) => {
    e.preventDefault();
    const text = newItem.trim();
    if (text) addItem.mutate(text);
  };

  return (
    <div className="rounded-md border border-divider">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <h3 className="min-w-0 truncate text-sm font-semibold">{checklist.name}</h3>
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {done}/{items.length}
        </span>
        {!readOnly ? (
          <Button
            variant="ghost"
            size="icon-xs"
            className="ml-auto shrink-0 text-muted-foreground hover:text-destructive"
            title="Remover checklist"
            onClick={onRemove}
          >
            <Trash2 className="size-3.5" />
          </Button>
        ) : null}
      </div>
      {/* Barra de progresso fina */}
      <div className="mx-3 h-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${done} de ${items.length} itens concluídos`}
        />
      </div>

      <ul className="mt-1 divide-y divide-divider">
        {items.map((item) => (
          <li key={item.id} className="group flex items-start gap-2.5 px-3 py-2">
            <Checkbox
              className="mt-0.5"
              checked={item.done}
              disabled={readOnly || mutating}
              onCheckedChange={() => toggle.mutate(item.id)}
              aria-label={item.text}
            />
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'text-sm break-words',
                  item.done && 'text-muted-foreground line-through'
                )}
              >
                {item.text}
              </p>
              {item.done && item.doneBy !== null ? (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Marcado por {userName(item.doneBy)}
                  {item.doneAt ? ` em ${formatDateTime(item.doneAt)}` : ''}
                </p>
              ) : null}
            </div>
            {!readOnly ? (
              <Button
                variant="ghost"
                size="icon-xs"
                className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                title="Remover item"
                disabled={mutating}
                onClick={() => removeItem.mutate(item.id)}
              >
                <X className="size-3.5" />
              </Button>
            ) : null}
          </li>
        ))}
        {items.length === 0 ? (
          <li className="px-3 py-2 text-sm text-muted-foreground">Checklist sem itens.</li>
        ) : null}
      </ul>

      {!readOnly ? (
        <form onSubmit={submitNewItem} className="flex gap-2 border-t border-divider p-2.5">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="Novo item…"
            className="h-8"
            disabled={addItem.isPending}
          />
          <Button
            type="submit"
            variant="outline"
            size="sm"
            className="h-8"
            disabled={addItem.isPending || !newItem.trim()}
          >
            <Plus className="size-3.5" />
            Item
          </Button>
        </form>
      ) : null}
      {error ? <p className="px-3 pb-2 text-sm text-destructive">{errorMessage(error)}</p> : null}
    </div>
  );
}

function AddChecklistDialog({
  ticketNumber,
  deskId,
  onClose,
  onAdded,
}: {
  ticketNumber: number;
  deskId: number;
  onClose: () => void;
  onAdded: () => void;
}) {
  const templatesQ = useQuery(deskChecklistTemplatesOptions(deskId));
  const [mode, setMode] = useState<'template' | 'adhoc'>('template');
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [itemsText, setItemsText] = useState('');

  const add = useMutation({
    mutationFn: async (body: TicketChecklistAddBody) => {
      const res = await api<TicketChecklist>(`/tickets/${ticketNumber}/checklists`, {
        method: 'POST',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      onAdded();
      onClose();
    },
  });

  const templates = (templatesQ.data ?? []).filter((t) => t.active);
  const adhocItems = itemsText
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const canSubmit =
    mode === 'template' ? templateId !== '' : name.trim() !== '' && adhocItems.length > 0;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    add.mutate(
      mode === 'template'
        ? { templateId: Number(templateId) }
        : { name: name.trim(), items: adhocItems }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Adicionar checklist">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === 'template' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('template')}
          >
            Do modelo da mesa
          </Button>
          <Button
            type="button"
            variant={mode === 'adhoc' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('adhoc')}
          >
            Avulso
          </Button>
        </div>

        {mode === 'template' ? (
          <div className="space-y-1.5">
            <Label htmlFor="checklist-template">Modelo</Label>
            {templatesQ.isPending ? (
              <Skeleton className="h-9 w-full" />
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Esta mesa não tem modelos de checklist. Crie um nas configurações da mesa ou use um
                checklist avulso.
              </p>
            ) : (
              <Select
                id="checklist-template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                <option value="">Selecione um modelo…</option>
                {templates.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name} ({t.items.length} {t.items.length === 1 ? 'item' : 'itens'})
                  </option>
                ))}
              </Select>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="checklist-adhoc-name">
                Nome <span className="text-destructive">*</span>
              </Label>
              <Input
                id="checklist-adhoc-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="checklist-adhoc-items">
                Itens (um por linha) <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="checklist-adhoc-items"
                rows={5}
                value={itemsText}
                onChange={(e) => setItemsText(e.target.value)}
                placeholder={'Conferir backup\nValidar acesso\n…'}
              />
            </div>
          </>
        )}

        {add.error ? <p className="text-sm text-destructive">{errorMessage(add.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={add.isPending || !canSubmit}>
            {add.isPending ? 'Adicionando…' : 'Adicionar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
